import { ArrowDownOutlined, ArrowUpOutlined, MoreOutlined } from '@ant-design/icons';
import { Dropdown, Menu, Popover, Tooltip } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';
import FocusIcon from '../../../images/focus.svg';
import { Direction, UpdatedLineages } from '../types';
import { EntityType } from '../../../types.generated';
import ManageLineageModal from './ManageLineageModal';
import { ENTITY_TYPES_WITH_MANUAL_LINEAGE } from '../../entity/shared/constants';
import { useTranslation } from '../../../i18n/useTranslation';

const DROPDOWN_Z_INDEX = 1;
const POPOVER_Z_INDEX = 2;
const getUnauthorizedText = (t: (key: string) => string) => t("You aren't authorized to edit lineage for this entity.");

const StyledImage = styled.img`
    width: 12px;
`;

const UnderlineWrapper = styled.span`
    text-decoration: underline;
    cursor: pointer;
`;

const StyledMenuItem = styled(Menu.Item)`
    padding: 0;
`;

const MenuItemContent = styled.div`
    padding: 5px 12px;
`;

function PopoverContent({
    centerEntity,
    direction,
    t,
}: {
    centerEntity?: () => void;
    direction: string;
    t?: (key: string) => string;
}) {
    return (
        <div>
            <UnderlineWrapper onClick={centerEntity}>{t ? t('Focus') : 'Focus'}</UnderlineWrapper>{' '}
            {t ? t(`on this entity to make ${direction} edits.`) : `on this entity to make ${direction} edits.`}
        </div>
    );
}

function getDownstreamDisabledPopoverContent(
    canEditLineage: boolean,
    isDashboard: boolean,
    centerEntity?: () => void,
    t?: (key: string) => string,
) {
    if (!canEditLineage) {
        return t ? getUnauthorizedText(t) : "You aren't authorized to edit lineage for this entity.";
    }
    if (isDashboard) {
        return t ? t('Dashboard entities have no downstream lineage') : 'Dashboard entities have no downstream lineage';
    }
    return <PopoverContent centerEntity={centerEntity} direction="downstream" t={t} />;
}

interface Props {
    entityUrn: string;
    refetchEntity: () => void;
    setUpdatedLineages: React.Dispatch<React.SetStateAction<UpdatedLineages>>;
    disableUpstream?: boolean;
    disableDownstream?: boolean;
    centerEntity?: () => void;
    showLoading?: boolean;
    menuIcon?: React.ReactNode;
    entityType?: EntityType;
    entityPlatform?: string;
    canEditLineage?: boolean;
    disableDropdown?: boolean;
}

export default function ManageLineageMenu({
    entityUrn,
    refetchEntity,
    setUpdatedLineages,
    disableUpstream,
    disableDownstream,
    centerEntity,
    showLoading,
    menuIcon,
    entityType,
    entityPlatform,
    canEditLineage,
    disableDropdown,
}: Props) {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [lineageDirection, setLineageDirection] = useState<Direction>(Direction.Upstream);

    function manageLineage(direction: Direction) {
        setIsModalVisible(true);
        setLineageDirection(direction);
    }

    const isCenterNode = !disableUpstream && !disableDownstream;
    const isDashboard = entityType === EntityType.Dashboard;
    const isDownstreamDisabled = disableDownstream || isDashboard || !canEditLineage;
    const isUpstreamDisabled = disableUpstream || !canEditLineage;
    const isManualLineageSupported = entityType && ENTITY_TYPES_WITH_MANUAL_LINEAGE.has(entityType);

    // if we don't show manual lineage options or the center node option, this menu has no options
    if (!isManualLineageSupported && isCenterNode) return null;

    return (
        <>
            <Tooltip title={disableDropdown ? getUnauthorizedText(t) : ''}>
                <div>
                    <Dropdown
                        overlayStyle={{ zIndex: DROPDOWN_Z_INDEX }}
                        disabled={disableDropdown}
                        overlay={
                            <Menu tabIndex={0}>
                                {isManualLineageSupported && (
                                    <>
                                        <StyledMenuItem
                                            key="0"
                                            tabIndex={0}
                                            onClick={() => manageLineage(Direction.Upstream)}
                                            disabled={isUpstreamDisabled}
                                        >
                                            <Popover
                                                content={
                                                    !canEditLineage ? (
                                                        getUnauthorizedText(t)
                                                    ) : (
                                                        <PopoverContent
                                                            centerEntity={centerEntity}
                                                            direction="upstream"
                                                            t={t}
                                                        />
                                                    )
                                                }
                                                overlayStyle={
                                                    isUpstreamDisabled
                                                        ? { zIndex: POPOVER_Z_INDEX }
                                                        : { display: 'none' }
                                                }
                                            >
                                                <MenuItemContent aria-label={t('Edit Upstream')}>
                                                    <ArrowUpOutlined />
                                                    &nbsp; {t('Edit Upstream')}
                                                </MenuItemContent>
                                            </Popover>
                                        </StyledMenuItem>
                                        <StyledMenuItem
                                            key="1"
                                            tabIndex={0}
                                            onClick={() => manageLineage(Direction.Downstream)}
                                            disabled={isDownstreamDisabled}
                                        >
                                            <Popover
                                                content={getDownstreamDisabledPopoverContent(
                                                    !!canEditLineage,
                                                    isDashboard,
                                                    centerEntity,
                                                    t,
                                                )}
                                                overlayStyle={
                                                    isDownstreamDisabled
                                                        ? { zIndex: POPOVER_Z_INDEX }
                                                        : { display: 'none' }
                                                }
                                            >
                                                <MenuItemContent aria-label={t('Edit Downstream')}>
                                                    <ArrowDownOutlined />
                                                    &nbsp; {t('Edit Downstream')}
                                                </MenuItemContent>
                                            </Popover>
                                        </StyledMenuItem>
                                    </>
                                )}
                                {!isCenterNode && centerEntity && (
                                    <Menu.Item key="2" onClick={centerEntity}>
                                        <StyledImage src={FocusIcon} alt={t('focus on entity')} />
                                        &nbsp; {t('Focus on Entity')}
                                    </Menu.Item>
                                )}
                            </Menu>
                        }
                        trigger={['click']}
                    >
                        {menuIcon || <MoreOutlined style={{ fontSize: 18 }} tabIndex={0} />}
                    </Dropdown>
                </div>
            </Tooltip>
            {isModalVisible && (
                <ManageLineageModal
                    entityUrn={entityUrn}
                    lineageDirection={lineageDirection}
                    closeModal={() => setIsModalVisible(false)}
                    refetchEntity={refetchEntity}
                    setUpdatedLineages={setUpdatedLineages}
                    showLoading={showLoading}
                    entityType={entityType}
                    entityPlatform={entityPlatform}
                />
            )}
        </>
    );
}
